import { LockKeyhole, Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Sidebar } from "@/components/Sidebar";
import { AppLoader } from "@/components/ui/app-loader";
import { useBilling } from "@/lib/api/query-hooks";

export type FeatureKey =
  | "ai_overlap_detection"
  | "ghost_subscription_flags"
  | "scenario_simulator"
  | "advanced_spend_charts"
  | "meridian_chat"
  | "renewal_risk_scoring"
  | "exports"
  | "future_stack_buy_decisions"
  | "deeper_ai_diagnostics"
  | "saved_decision_history"
  | "founder_feedback_lane"
  | "cross_app_command_center"
  | "multi_product_automation"
  | "concierge_workflows";

const featureLabels: Record<FeatureKey, string> = {
  ai_overlap_detection: "AI overlap detection",
  ghost_subscription_flags: "ghost subscription flags",
  scenario_simulator: "scenario simulator",
  advanced_spend_charts: "advanced spend charts",
  meridian_chat: "Meridian chat",
  renewal_risk_scoring: "renewal risk scoring",
  exports: "reports and exports",
  future_stack_buy_decisions: "Future Stack buy decisions",
  deeper_ai_diagnostics: "deeper AI diagnostics",
  saved_decision_history: "saved decision history",
  founder_feedback_lane: "founder feedback lane",
  cross_app_command_center: "cross-app command center",
  multi_product_automation: "multi-product automation",
  concierge_workflows: "concierge workflows",
};

export const useEntitlements = () => {
  const billing = useBilling();
  const entitlements = billing.data?.entitlements ?? billing.data?.access?.entitlements ?? [];
  const enabledFeatures = new Set(entitlements.filter((item) => item.enabled).map((item) => item.featureKey));

  const hasFeature = (featureKey: FeatureKey) => enabledFeatures.has(featureKey);
  const requiredPlanFor = (featureKey: FeatureKey) => {
    const entitlement = entitlements.find((item) => item.featureKey === featureKey);
    if (!entitlement) return "pulse";
    if ("requiredPlanKey" in entitlement) return entitlement.requiredPlanKey;
    if ("planKey" in entitlement) return entitlement.planKey;
    return "pulse";
  };

  return {
    ...billing,
    planKey: billing.data?.access?.planKey || billing.data?.billing?.planKey || "flow",
    limits: billing.data?.access?.limits ?? {
      maxSubscriptions: 5,
      maxTeams: 1,
      apiRequestsPerMinute: 60,
      assistantRequestsPerMinute: 10,
    },
    hasFeature,
    requiredPlanFor,
  };
};

export const LockedFeaturePanel = ({
  featureKey,
  compact = false,
}: {
  featureKey: FeatureKey;
  compact?: boolean;
}) => {
  const navigate = useNavigate();
  const entitlements = useEntitlements();
  const requiredPlan = entitlements.requiredPlanFor(featureKey);
  const label = featureLabels[featureKey];

  return (
    <div className={`rounded-2xl border border-orizonte-lime/20 bg-orizonte-lime/[0.045] ${compact ? "p-4" : "p-6 sm:p-8"}`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-orizonte-lime/25 bg-orizonte-lime/10 text-orizonte-lime">
            <LockKeyhole className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-orizonte-lime">{requiredPlan} feature</p>
            <h2 className="mt-1 text-lg font-semibold text-orizonte-text">Unlock {label}</h2>
            <p className="mt-1 text-sm leading-relaxed text-orizonte-textMuted">
              Your current plan is {entitlements.planKey}. Upgrade to use this workspace feature.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate(`/pricing?plan=${requiredPlan}`)}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-orizonte-lime px-4 py-3 text-sm font-semibold text-black transition-colors hover:bg-orizonte-lime/90"
        >
          <Sparkles className="h-4 w-4" />
          View plans
        </button>
      </div>
    </div>
  );
};

export const FeaturePageGate = ({
  featureKey,
  children,
}: {
  featureKey: FeatureKey;
  children: ReactNode;
}) => {
  const entitlements = useEntitlements();
  if (entitlements.isLoading) {
    return (
      <div className="min-h-screen bg-orizonte-bg flex items-center justify-center">
        <AppLoader label="Checking plan access..." />
      </div>
    );
  }

  if (entitlements.hasFeature(featureKey)) return <>{children}</>;

  return (
    <div className="flex min-h-screen bg-orizonte-bg">
      <Sidebar />
      <main className="flex-1 p-4 pt-20 sm:p-8 md:ml-64 md:pt-8">
        <div className="mx-auto max-w-3xl">
          <LockedFeaturePanel featureKey={featureKey} />
        </div>
      </main>
    </div>
  );
};
